/**
 * BottomNav.jsx — Barre de navigation mobile (bas d'écran)
 *
 * Affichée uniquement quand un utilisateur est connecté (authUser != null).
 * Écoute deux événements pour rester synchronisée en temps réel :
 *  - 'storage'               : changement localStorage depuis un autre onglet
 *  - AUTH_USER_CHANGED_EVENT : changement de session depuis le même onglet
 *
 * Ajoute/retire la classe `vx-has-bottom-nav` sur <body> pour que les pages
 * puissent adapter leur padding inférieur quand la barre est visible.
 *
 * Le NotificationCenter est rendu avec bottomBarMode=true : il produit deux boutons
 * (Notif + Messages) avec `display:contents` pour s'intégrer dans la grille CSS.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import NotificationCenter from '../notification/Notification.jsx';
import { AUTH_USER_CHANGED_EVENT } from '../../utils/authEvents';
import './BottomNav.css';

function getStoredAuthUser() {
	try {
		const raw = window.localStorage.getItem('exp-auth-user');
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

function BottomNav() {
	const [authUser, setAuthUser] = useState(() => getStoredAuthUser());
	const location = useLocation();

	useEffect(() => {
		const handleStorage = () => setAuthUser(getStoredAuthUser());
		const handleAuthChange = () => setAuthUser(getStoredAuthUser());
		window.addEventListener('storage', handleStorage);
		window.addEventListener(AUTH_USER_CHANGED_EVENT, handleAuthChange);
		handleStorage();
		return () => {
			window.removeEventListener('storage', handleStorage);
			window.removeEventListener(AUTH_USER_CHANGED_EVENT, handleAuthChange);
		};
	}, []);

	useEffect(() => {
		if (!authUser) {
			document.body.classList.remove('vx-has-bottom-nav');
			return undefined;
		}
		document.body.classList.add('vx-has-bottom-nav');
		return () => document.body.classList.remove('vx-has-bottom-nav');
	}, [authUser]);

	if (!authUser) return null;

	const avatarUrl = authUser?.profilePhotoUrl || '';
	const initials = `${(authUser?.firstName || authUser?.prenom || '').charAt(0)}${(authUser?.lastName || authUser?.nom || '').charAt(0)}`.toUpperCase();
	const path = location.pathname;

	return (
		<nav className="vx-bottom-nav" aria-label="Navigation mobile">
			{/* ordre 1 */}
			<Link
				to="/search"
				className={`vx-bottom-nav-item vx-bn-search${path === '/search' ? ' is-active' : ''}`}
				aria-current={path === '/search' ? 'page' : undefined}
			>
				<span className="vx-bottom-nav-icon-wrap">
					<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
						<circle cx="10" cy="10" r="6.5" stroke="currentColor" strokeWidth="1.5" />
						<line x1="15" y1="15" x2="20" y2="20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
					</svg>
				</span>
				<span>Recherche</span>
			</Link>

			{/* ordres 2 (notif) et 4 (msg) — via display:contents dans NotificationCenter */}
			<NotificationCenter authUser={authUser} bottomBarMode />

			{/* ordre 3 — centre, plus grand */}
			<Link
				to="/profile"
				className={`vx-bottom-nav-item vx-bottom-nav-profile vx-bn-profile${path === '/profile' ? ' is-active' : ''}`}
				aria-current={path === '/profile' ? 'page' : undefined}
			>
				<span className="bn-avatar bn-avatar-lg" aria-hidden="true">
					{avatarUrl ? <img src={avatarUrl} alt="" /> : (initials || 'U')}
				</span>
				<span>Profil</span>
			</Link>

			{/* ordre 5 */}
			<Link
				to="/assistant-ia"
				className={`vx-bottom-nav-item vx-bn-assistant${path === '/assistant-ia' ? ' is-active' : ''}`}
				aria-current={path === '/assistant-ia' ? 'page' : undefined}
			>
				<span className="vx-bottom-nav-icon-wrap">
					<svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
						<path d="M11 3C7 3 4 6 4 10c0 2.5 1.2 4.7 3 6.1V19l3-1.5 1 .5 1-.5 3 1.5v-2.9C16.8 14.7 18 12.5 18 10c0-4-3-7-7-7Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
						<circle cx="8.5" cy="10" r="1" fill="currentColor"/>
						<circle cx="11" cy="10" r="1" fill="currentColor"/>
						<circle cx="13.5" cy="10" r="1" fill="currentColor"/>
					</svg>
				</span>
				<span>Assistant</span>
			</Link>
		</nav>
	);
}

export default BottomNav;
